import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Tag, ArrowRight, Clock3, CalendarDays } from "lucide-react";
import {
  getOffers,
  getPopupConfig,
  isOfferLive,
  wasPopupSeen,
  markPopupSeen,
} from "@/lib/offersStore";

const formatDays = (days) => {
  if (!days || days.length === 0) return null;
  const sorted = [...days].sort((a, b) => a - b);
  const isRange = sorted.every((v, i, arr) => i === 0 || v === arr[i - 1] + 1);
  if (isRange && sorted.length >= 2) {
    const full = ["", "lun", "mar", "mer", "jeu", "ven", "sam", "dim"];
    return `${full[sorted[0]]} – ${full[sorted[sorted.length - 1]]}`;
  }
  return sorted.join("·");
};

export const OfferPopup = () => {
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    // Mode "Aperçu popup" — depuis l'admin (?preview-popup=1 ou =<offerId>).
    // Bypass total : enabled, isLive, wasSeen. Ne marque PAS comme vu.
    let previewOfferId = null;
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get("preview-popup");
      if (raw) previewOfferId = raw === "1" ? "__use_cfg__" : raw;
    } catch {
      /* ignore */
    }

    const cfg = getPopupConfig();
    const offers = getOffers();

    if (previewOfferId) {
      const targetId =
        previewOfferId === "__use_cfg__" ? cfg.offer_id : previewOfferId;
      const target = offers.find((o) => o.id === targetId);
      if (target) {
        const t = setTimeout(() => setOffer(target), 200);
        return () => clearTimeout(t);
      }
      return;
    }

    if (!cfg.enabled) return;

    const target = offers.find((o) => o.id === cfg.offer_id);
    if (!target) return;
    if (!target.popup_enabled) return;
    if (!isOfferLive(target)) return;
    if (wasPopupSeen(target.id, target.popup_frequency)) return;

    const t = setTimeout(() => {
      setOffer(target);
      markPopupSeen(target.id, target.popup_frequency);
    }, cfg.delay_ms ?? 1500);

    return () => clearTimeout(t);
  }, []);

  if (!offer) return null;

  const days = formatDays(offer.active_days);
  const hours =
    offer.start_time && offer.end_time
      ? `${offer.start_time.replace(":", "h")} – ${offer.end_time.replace(":", "h")}`
      : null;

  return (
    <div
      data-testid="offer-popup-root"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-6 bg-black/65 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="offer-popup-title"
    >
      <div className="relative w-full max-w-md rounded-2xl border border-[#FF9500]/30 bg-[#121212] shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden">
        <button
          type="button"
          aria-label="Fermer"
          data-testid="offer-popup-close"
          onClick={() => setOffer(null)}
          className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-white/70 hover:text-white hover:bg-white/5 transition-colors z-10"
        >
          <X size={16} />
        </button>

        {offer.media_url && offer.media_type === "image" && (
          <div className="relative h-40 sm:h-48 overflow-hidden border-b border-[#262626]">
            <img
              src={offer.media_url}
              alt={offer.title}
              loading="lazy" decoding="async"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/80 to-transparent" />
          </div>
        )}

        <div className="p-5 sm:p-6">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-[#FF9500]/30 bg-[#FF9500]/10 px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] text-[#FF9500] font-street">
            <Tag size={11} />
            Offre du moment
          </div>

          <h2
            id="offer-popup-title"
            className="font-street mt-3 text-2xl sm:text-3xl tracking-wide leading-tight"
          >
            {offer.title}
          </h2>

          <div className="mt-1 font-display text-4xl text-[#FF9500] leading-none">
            {offer.price}
          </div>

          {offer.description && (
            <p className="mt-3 text-sm text-[#D4D4D8] leading-relaxed">
              {offer.description}
            </p>
          )}

          {(days || hours) && (
            <div className="mt-3 flex flex-wrap gap-3 text-[11px] uppercase tracking-[0.22em] font-street">
              {days && (
                <span className="inline-flex items-center gap-1.5 text-[#FFC447]">
                  <CalendarDays size={12} />
                  {days}
                </span>
              )}
              {hours && (
                <span className="inline-flex items-center gap-1.5 text-[#FFC447]">
                  <Clock3 size={12} />
                  {hours}
                </span>
              )}
            </div>
          )}

          <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
            <Link
              to={offer.cta_target || `/offres#${offer.id}`}
              data-testid="offer-popup-cta"
              onClick={() => setOffer(null)}
              className="flex-1 font-street inline-flex items-center justify-center gap-2 rounded-md bg-[#FF9500] px-4 py-3 text-sm tracking-wider text-black shadow-[0_4px_0_#7a4900] hover:-translate-y-0.5 transition-all"
            >
              {offer.cta_label || "Voir l'offre"}
              <ArrowRight size={14} />
            </Link>
            <button
              type="button"
              onClick={() => setOffer(null)}
              className="font-street inline-flex items-center justify-center rounded-md border border-white/10 px-4 py-3 text-sm tracking-wider text-white/70 hover:bg-white/5 transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfferPopup;
